import { useState } from 'react';
import {
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  MinusCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  PlayCircle,
  Check,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  useAdminAppeals,
  useStartReview,
  useApproveAppeal,
  useRejectAppeal,
  useAppeal,
} from '@/hooks/use-appeals';
import { cn } from '@/lib/utils';

const STATUS_CONFIG = {
  submitted: {
    label: 'Submitted',
    icon: Clock,
    color: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  },
  under_review: {
    label: 'Under Review',
    icon: Eye,
    color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle,
    color: 'bg-green-500/10 text-green-500 border-green-500/20',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    color: 'bg-red-500/10 text-red-500 border-red-500/20',
  },
  withdrawn: {
    label: 'Withdrawn',
    icon: MinusCircle,
    color: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  },
};

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(dateString) {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatsCards({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      <Card>
        <CardContent className="pt-6">
          <div className="text-2xl font-bold text-blue-500">{stats.submitted || 0}</div>
          <p className="text-sm text-muted-foreground">Pending</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="text-2xl font-bold text-yellow-500">{stats.under_review || 0}</div>
          <p className="text-sm text-muted-foreground">Under Review</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="text-2xl font-bold text-green-500">{stats.approved || 0}</div>
          <p className="text-sm text-muted-foreground">Approved</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="text-2xl font-bold text-red-500">{stats.rejected || 0}</div>
          <p className="text-sm text-muted-foreground">Rejected</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="text-2xl font-bold text-gray-500">{stats.withdrawn || 0}</div>
          <p className="text-sm text-muted-foreground">Withdrawn</p>
        </CardContent>
      </Card>
    </div>
  );
}

function ReviewDialog({ appeal, open, onOpenChange }) {
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [reverseAction, setReverseAction] = useState(true);
  const [reversalNotes, setReversalNotes] = useState('');
  const [action, setAction] = useState(null);

  const { mutate: startReview, isPending: isStarting } = useStartReview();
  const { mutate: approveAppeal, isPending: isApproving } = useApproveAppeal();
  const { mutate: rejectAppeal, isPending: isRejecting } = useRejectAppeal();

  const handleStartReview = () => {
    startReview(appeal.appealId, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const handleApprove = () => {
    approveAppeal(
      {
        appealId: appeal.appealId,
        reviewNotes,
        reverseAction,
        reversalNotes,
      },
      {
        onSuccess: () => {
          setReviewNotes('');
          setReversalNotes('');
          setAction(null);
          onOpenChange(false);
        },
      }
    );
  };

  const handleReject = () => {
    rejectAppeal(
      {
        appealId: appeal.appealId,
        reviewNotes,
        rejectionReason,
      },
      {
        onSuccess: () => {
          setReviewNotes('');
          setRejectionReason('');
          setAction(null);
          onOpenChange(false);
        },
      }
    );
  };

  const isPending = isStarting || isApproving || isRejecting;
  const status = STATUS_CONFIG[appeal?.status];
  const StatusIcon = status?.icon || AlertCircle;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Review Appeal
            {status && (
              <Badge variant="outline" className={cn('ml-2', status.color)}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {status.label}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {appeal?.appealId} - {appeal?.appellantUsername || appeal?.appellantWallet?.slice(0, 10) + '...'}
          </DialogDescription>
        </DialogHeader>

        {appeal && (
          <div className="space-y-6">
            {/* Original Action */}
            <div>
              <h4 className="font-semibold mb-2">Original Moderation Action</h4>
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <Badge variant="destructive">
                    {appeal.originalActionSnapshot?.actionType === 'user_suspended'
                      ? 'Suspension'
                      : 'Ban'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Action ID:</span>
                  <span className="font-mono text-xs">{appeal.moderationActionStringId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Issued by:</span>
                  <span>{appeal.originalActionSnapshot?.adminUsername || 'Admin'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span>{formatDateTime(appeal.originalActionSnapshot?.createdAt)}</span>
                </div>
                {appeal.originalActionSnapshot?.duration && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span>{appeal.originalActionSnapshot.duration} hours</span>
                  </div>
                )}
                <div className="pt-2 border-t">
                  <span className="text-muted-foreground">Reason:</span>
                  <p className="mt-1">{appeal.originalActionSnapshot?.reason}</p>
                </div>
              </div>
            </div>

            {/* User's Appeal */}
            <div>
              <h4 className="font-semibold mb-2">User's Appeal</h4>
              <div className="rounded-lg border p-4">
                <p className="text-sm whitespace-pre-wrap">{appeal.reason}</p>
              </div>
            </div>

            {/* Additional Evidence */}
            {appeal.additionalEvidence && (
              <div>
                <h4 className="font-semibold mb-2">Additional Evidence</h4>
                <div className="rounded-lg border p-4">
                  <p className="text-sm whitespace-pre-wrap">{appeal.additionalEvidence}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {['submitted', 'under_review'].includes(appeal.status) && !action && (
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {appeal.status === 'submitted' && (
                  <Button
                    variant="outline"
                    onClick={handleStartReview}
                    disabled={isPending}
                  >
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Start Review
                  </Button>
                )}
                <Button
                  variant="default"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setAction('approve')}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setAction('reject')}
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </div>
            )}

            {/* Approve Form */}
            {action === 'approve' && (
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-semibold">Approve Appeal</h4>

                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <Label htmlFor="reverse">Reverse Original Action</Label>
                    <p className="text-sm text-muted-foreground">
                      Remove the suspension/ban from the user
                    </p>
                  </div>
                  <Switch
                    id="reverse"
                    checked={reverseAction}
                    onCheckedChange={setReverseAction}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reviewNotes">Review Notes (Optional)</Label>
                  <Textarea
                    id="reviewNotes"
                    placeholder="Internal notes about this review..."
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={3}
                  />
                </div>

                {reverseAction && (
                  <div className="space-y-2">
                    <Label htmlFor="reversalNotes">Message to User (Optional)</Label>
                    <Textarea
                      id="reversalNotes"
                      placeholder="Message to include in user notification..."
                      value={reversalNotes}
                      onChange={(e) => setReversalNotes(e.target.value)}
                      rows={2}
                    />
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setAction(null)}>
                    Cancel
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleApprove}
                    disabled={isPending}
                  >
                    {isApproving ? 'Approving...' : 'Confirm Approval'}
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* Reject Form */}
            {action === 'reject' && (
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-semibold">Reject Appeal</h4>

                <div className="space-y-2">
                  <Label htmlFor="rejectionReason">
                    Rejection Reason <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="rejectionReason"
                    placeholder="Explain why this appeal is being rejected..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={3}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    This will be shown to the user.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reviewNotesReject">
                    Internal Review Notes (Optional)
                  </Label>
                  <Textarea
                    id="reviewNotesReject"
                    placeholder="Internal notes about this review..."
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={2}
                  />
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setAction(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleReject}
                    disabled={!rejectionReason || isPending}
                  >
                    {isRejecting ? 'Rejecting...' : 'Confirm Rejection'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminAppeals() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAppeal, setSelectedAppeal] = useState(null);

  const { appeals, stats, pagination, isLoading, refetch } = useAdminAppeals({
    page,
    limit: 20,
    status: statusFilter === 'all' ? null : statusFilter,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Appeals Queue</h1>
          <p className="text-muted-foreground">
            Review and manage user moderation appeals
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <StatsCards stats={stats} />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="submitted">Pending</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="withdrawn">Withdrawn</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Appeals Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : appeals.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center">
              <AlertCircle className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No appeals found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Appeal ID</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appeals.map((appeal) => {
                  const status = STATUS_CONFIG[appeal.status];
                  const StatusIcon = status?.icon || AlertCircle;

                  return (
                    <TableRow key={appeal.appealId}>
                      <TableCell className="font-mono text-xs">
                        {appeal.appealId}
                      </TableCell>
                      <TableCell>
                        {appeal.appellantUsername || (
                          <span className="font-mono text-xs">
                            {appeal.appellantWallet?.slice(0, 10)}...
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {appeal.moderationActionType === 'user_suspended'
                            ? 'Suspension'
                            : 'Ban'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn(status?.color)}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(appeal.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedAppeal(appeal)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
            disabled={page === pagination.pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Review Dialog */}
      <ReviewDialog
        appeal={selectedAppeal}
        open={!!selectedAppeal}
        onOpenChange={(open) => !open && setSelectedAppeal(null)}
      />
    </div>
  );
}
